import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import {AuthorizeVisibilityDirective, EsvgFiles} from 'frontier';

import {MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup,} from '@angular/material/button-toggle';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {FormsModule} from '@angular/forms';
import {
  TabContentComponent
} from 'frontier/browserkit/src/lib/components/home-without-gl/tabs/tab/tab-content.component';
import {ITab} from 'frontier/browserkit/src/lib/components/home-without-gl/tabs/tab.interface';
import {MatIcon} from '@angular/material/icon';
import {Subject} from 'rxjs';
import {OnBoardingStateViewService, TOnBoardingStateRecord} from '../../../api/generated';
import {AUTHORIZATION_CONFIG, ProtectedView} from '../../../config/authorization.config';
import {HttpErrorResponse} from '@angular/common/http';
import {MatProgressBar} from '@angular/material/progress-bar';

interface IOnboardingView {
  key: keyof TOnBoardingStateRecord;
  label: string;
  completed: boolean;
  helpNote: string;
  componentName: ProtectedView;
}

@Component({
  selector: 'app-onboarding-overview',
  standalone: true,
  imports: [
    MatButtonToggle,
    MatButtonToggleGroup,
    MatSlideToggle,
    FormsModule,
    TabContentComponent,
    MatIcon,
    AuthorizeVisibilityDirective,
    MatProgressBar,
  ],
  templateUrl: './onboarding-overview.component.html',
  styleUrl: './onboarding-overview.component.scss'
})
export class OnboardingOverviewComponent implements AfterViewInit{
  protected readonly AUTHORIZATION_CONFIG: Record<ProtectedView, string[]> = AUTHORIZATION_CONFIG;

  private _onBoardingStateViewService: OnBoardingStateViewService = inject(OnBoardingStateViewService);
  cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;

  selectedOnboardingTab: WritableSignal<IOnboardingView> = model<IOnboardingView>(null);

  completedToggle$: Subject<object> = new Subject<object>();

  tabData: Signal<ITab> = computed<ITab>((): ITab => {
    return {
      label: this.selectedOnboardingTab().label,
      componentName: this.selectedOnboardingTab().componentName,
      tabId: this.selectedOnboardingTab().key,
    };
  })

  authorized: WritableSignal<boolean | null> = signal<boolean | null>(null);

  onboardingTabs: WritableSignal<IOnboardingView[]> = model<IOnboardingView[]>([
    {
      key: 'DataOfficeBase',
      label: 'Datenannahmestellen Import',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/00_Datenannahmestellen_Import.csv\nDie Adresse der AOK Baden-Württemberg muss ' +
        'pro Kunde angepasst werden.\nNach Überprüfung auf Vollständigkeit ist der Schritt abgeschlossen.',
      componentName: ProtectedView.DataOfficeBaseTableComponent,
    },
    {
      key: 'PayerBase',
      label: 'Kostenträger',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/01_Kostenträger.csv und 02_Kostenträger_Mapping.csv\n' +
        'Nach Überprüfung auf Vollständigkeit und der Kundensicht Kostenträger in den Optionen ist der Schritt ' +
        'abgeschlossen.',
      componentName: ProtectedView.PayerTableComponent,
    },
    {
      key: 'ItemBase',
      label: 'Positionen',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/03_Positionen_*.csv\nGgf. müssen weitere Positionsnummern angelegt werden. ' +
        'Die Stammdaten-Datei 03_Positionen_Vollständig.csv ist in diesem Fall zu erweitern.',
      componentName: ProtectedView.ItemTableComponent,
    },
    {
      key: 'ItemGroupType',
      label: 'Tarifgruppen',
      completed: false,
      helpNote: 'Die Tarifgruppen RTW, KTW, NEF, NAW und SONSTIGE exakt so anlegen. Das Berichtswesen ordnet die ' +
        'Erlöse über diese Namen zu.',
      componentName: ProtectedView.ItemGroupTypeTableComponent,
    },
    {
      key: 'ItemGroupBase',
      label: 'Tarife Import',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/04_Tarife_Template.csv\nTarife sind meist so individuell, dass eine eigene CSV ' +
        'nötig ist. Das Template liefert Basis-Tarife für KTW, RTW und NEF. Eine vollständige Historie ist unnötig; ' +
        'Tarifänderungen folgen im nächsten Schritt.',
      componentName: ProtectedView.ItemGroupBaseTableComponent,
    },
    {
      key: 'ItemGroupDetails',
      label: 'Tarife',
      completed: false,
      helpNote: 'Überprüfung der Tarif-Einstellungen. Die Kostenstelle ist meist leer, da sie über das Geschäftsfeld ' +
        'gesetzt wird. Konten werden hier oder über das Mapping definiert. Zeitliche Tarif- und Preisänderungen ' +
        'können hier eingestellt werden. Die View ist für weitere Anpassungen auch in den Optionen verfügbar.',
      componentName: ProtectedView.TariffChangesComponent,
    },
    {
      key: 'Businessareas',
      label: 'GF Import',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/05_GF_Import.csv\nAnpassung der Konten und Kostenstellen, soweit bekannt. In ' +
        'der nachfolgenden View kann das Mapping finalisiert werden.',
      componentName: ProtectedView.BusinessAreasTableComponent,
    },
    {
      key: 'BusinessareaMapping',
      label: 'Geschäftsfeld-Mapping',
      completed: false,
      helpNote: 'Überprüfung des Geschäftsfeld-Mappings. Meist müssen ein normales Mapping und eines für ' +
        'uneinbringlich konfiguriert werden. Ausnahmen für Tarifpositionen können hier erstellt werden.',
      componentName: ProtectedView.ItemPriceMappingTableComponent,
    },
    {
      key: 'AlternativePayer',
      label: 'Alternative Kostenträger',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/06_AlternativeKostenträger.csv\nDie CSV-Datei muss vom Kunden mit ' +
        'Debitor-Nummern sowie weiteren Kostenträgern (Krankenhäusern, Landratsämtern etc.) erweitert werden. Diese' +
        ' Liste kann in den Optionen verwaltet werden.',
      componentName: ProtectedView.AlternativePayerTableComponent,
    },
    {
      key: 'PayerAccount',
      label: 'Kostenträger',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/07_KostenträgerDebitoren.csv\nDie CSV-Datei muss vom Kunden mit Debitor-Nummern ' +
        'erweitert werden. Weitere Änderungen können in den Optionen vorgenommen werden.',
      componentName: ProtectedView.PayerHiddenTableComponent,
    },
    {
      key: 'BookingOptions',
      label: 'Buchhaltung',
      completed: false,
      helpNote: 'JSON Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/08_BasisVerbuchung.json\nPflicht: Debitor-Konten, Rechnungsnummern und ' +
        'Aggregationen.\nDie Mandantennummer stammt aus der Kundenbuchhaltung. Diamant- und EXTF-Felder nur bei ' +
        'Nutzung des Exports setzen, ggf. später in den Optionen.',
      componentName: ProtectedView.BookingOptionsConfigFormComponent,
    },
    {
      key: 'CustomerBaseConfig',
      label: 'Stammdaten',
      completed: false,
      helpNote: 'Hier werden die Stammdaten eingetragen, die als Basis für die Rechnungserstellung verwendet werden.',
      componentName: ProtectedView.CustomerBasedataConfigFormComponent,
    },
    {
      key: 'DTAServiceConfig',
      label: 'DTA',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/09_DTA_Basis.csv\nDer Tabelleneintrag muss ggf. angepasst werden. Die dortigen ' +
        'Angaben werden in die DTA geschrieben.',
      componentName: ProtectedView.DtaServiceConflictTableComponent,
    },
    {
      key: 'GlobalTemplateConfig',
      label: 'Globale Templates',
      completed: false,
      helpNote: 'JSON Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/10_BasisRechnungstemplates.json\nDie Anpassung der Daten erfolgt in der' +
        ' nächsten View.',
      componentName: ProtectedView.GlobalTemplateConfigFormComponent,
    },
    {
      key: 'BaseInvoiceTemplateConfig',
      label: 'Template Import',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/11_RechnungstemplatesBasis.csv\nDie Anpassung der Daten erfolgt in der nächsten ' +
        'View.',
      componentName: ProtectedView.BaseInvoiceTemplateConfigTableComponent,
    },
    {
      key: 'LanguageImport',
      label: 'Sprachen',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/12_BasicSprachen*.csv (Deutsch oder Englisch, je nach Kunde nur Deutsch oder ' +
        'beides)\nDie Sprachen für Rechnungen, Mahnungen und Stornos können jederzeit bearbeitet werden. Die ' +
        'Übersetzungen werden in der nächsten View festgelegt.',
      componentName: ProtectedView.LanguageImportTableComponent,
    },
    {
      key: 'DictionaryImport',
      label: 'Wörterbuch',
      completed: false,
      helpNote: 'Hier werden die Formulierungen für Rechnungen, Mahnungen und Stornos je Sprache übersetzt; ' +
        'Änderungen sind jederzeit möglich. Deutsch muss nicht importiert werden; falls doch, muss es gepflegt ' +
        'werden. Englisch besser über die View Sprachen importieren (aktuellste Version). Weitere Sprachen füllt ' +
        'ggf. der Kunde.',
      componentName: ProtectedView.DictionaryImportTableComponent,
    },
    {
      key: 'DetailedInvoiceTemplateConfig',
      label: 'Rechnungsvorlagen',
      completed: false,
      helpNote: 'Die Rechnungstemplates können jederzeit über die Optionen angepasst werden. Zuerst sollte das ' +
        'Standard-Template mit allen Informationen angereichert werden. Im Anschluss können pro Rechnungsart ' +
        'Anpassungen vorgenommen werden. Die finale Abnahme sollte zusammen mit dem Kunden in dieser View erfolgen.',
      componentName: ProtectedView.TemplatesControl,
    },
    {
      key: 'DunningConfigImport',
      label: 'Mahnungskonfiguration',
      completed: false,
      helpNote: 'Die verschiedenen Mahntemplates können später jederzeit verwaltet werden.',
      componentName: ProtectedView.DunningConfigImportTableComponent,
    },
    {
      key: 'DunningConfig',
      label: 'Mahnstufen',
      completed: false,
      helpNote: 'Die Mahnkonfiguration ist optional und kann auch nachträglich in den Optionen erfolgen. Eine ' +
        'Mahnkonfiguration kann einem oder mehreren Rechnungstemplates zugeordnet werden. Jede Mahnkonfiguration ' +
        'enthält mehrere Mahnstufen mit jeweils eigener Template-Konfiguration.',
      componentName: ProtectedView.DunningLevelImportTableComponent,
    },
    {
      key: 'InvoiceAutomation',
      label: 'Rechnungsautomation',
      completed: false,
      helpNote: 'CSV Import: OneDrive-SharedLibraries-KPI4meOHG/5 Kundenprojekte - ' +
        'General/000_ARK_Stammdaten/14_Rechnungsautomation.csv\nRechnungen entstehen bei erfüllter Bedingung, ' +
        'z. B. 50 offene Fahrten je Kostenträgertyp. NotModifiedHours: Zeit ohne Änderung; Delay: Wartezeit vor ' +
        'Erzeugung. Die Spalte Aktiv schaltet Regeln ein und aus.',
      componentName: ProtectedView.InvoiceAutomationImportComponent,
    },
    {
      key: 'ConfigLanguageImport',
      label: 'Templatekonfiguration',
      completed: false,
      helpNote: 'Die Rechnungstemplates können später jederzeit über einen gesonderten Editor aufgebaut werden.',
      componentName: ProtectedView.ConfigLanguageImportTableComponent,
    },
    {
      key: 'StationConfig',
      label: 'Rettungswachen',
      completed: false,
      helpNote: 'Die Rettungswachen können entweder importiert oder manuell eingetragen werden. Die ID muss eindeutig' +
        ' sein und mit der Rettungswache aus dem Quellsystem übereinstimmen.',
      componentName: ProtectedView.RescueStationComponent,
    },
    {
      key: 'OpenItemImport',
      label: 'Offene Posten',
      completed: false,
      helpNote: 'Der Import offener Posten ist nur über CSV-Dateien möglich. Der Modus setzt Grundeinstellungen. ' +
        'Alle weiteren Felder setzen Spalten-Namen oder -Indizes, damit der ARK die CSV lesen kann. Über den ' +
        'Debitor-Filter können offene Posten via Regex gefiltert werden. Die Formatierung definiert Zahlen- und ' +
        'Datumsformate.',
      componentName: ProtectedView.OpenItemImportConfigFormComponent,
    },
    {
      key: 'EPostConfig',
      label: 'E-Post',
      completed: false,
      helpNote: 'Die E-Post wird durch Eingabe der EKP und die Aktion SMS-Code anfordern aktiviert. Die Übertragung ' +
        'der E-Post-Informationen auf einen anderen Mandanten erfolgt in einer versteckten View, die globale ' +
        'Admin-Rechte benötigt.',
      componentName: ProtectedView.EpostConfigWrapperComponent,
    },
  ]);

  sortedTabs: Signal<IOnboardingView[]> = computed<IOnboardingView[]>((): IOnboardingView[] => {
    const unsortedTabs: IOnboardingView[] = this.onboardingTabs();
    return unsortedTabs.filter((tab: IOnboardingView): boolean => !tab.completed)
      .concat(unsortedTabs.filter((tab: IOnboardingView): boolean => tab.completed));
  })

  constructor() {
    effect((): void => {
      const selectedOnboardingTab: IOnboardingView = this.selectedOnboardingTab();
      localStorage.setItem('onboardingTab', JSON.stringify(selectedOnboardingTab));
    })
  }

  ngAfterViewInit(): void {
    this._onBoardingStateViewService.onBoardingStateViewGetData().subscribe({
      next: (res: any): void => {
        if (res) {
          let updatedTabs: IOnboardingView[] = this.onboardingTabs();
          updatedTabs = updatedTabs.map((tab: IOnboardingView): IOnboardingView => {
            return {...tab, completed: res[tab.key.toLowerCase()]} as IOnboardingView;
          });
          this.onboardingTabs.set(updatedTabs);
          this.authorized.set(true);
        }
      },
      error: (res: HttpErrorResponse): void => {
        if (res.status === 403) {
          this.authorized.set(false);
        }
      }
    });

    const selectedOnboardingTab: IOnboardingView = JSON.parse(localStorage.getItem('onboardingTab'));
    if (selectedOnboardingTab) {
      this.selectedOnboardingTab.set(selectedOnboardingTab);
    }
  }

  onSelectedOnboardingTabChange(evt: MatButtonToggleChange): void {
    this.selectedOnboardingTab.set(evt.value);
  }

  onModelChange(evt: boolean): void  {
    const payload: TOnBoardingStateRecord = {};
    this.onboardingTabs().forEach((tab: IOnboardingView): void => {payload[tab.key] = tab.completed});
    this._onBoardingStateViewService.onBoardingStateViewChangeData({states: payload})
      .subscribe({
        next: (res: boolean): void => {
          if (res) {
            this.completedToggle$.next({});
            if (evt) {
              this.selectedOnboardingTab.set(this.sortedTabs()[0]);
            }
          }
        },
        error: (): void => {
          this.selectedOnboardingTab().completed = !this.selectedOnboardingTab().completed;
        }
      })
  }

  addLinebreaks(helpNote: string): string {
    return helpNote.replace(/\n/g, '<br>');
  }
}
